import { AxiosResponse } from "axios";
import { IGlobalSearchApi } from "../api/IGlobalSearchApi";
import ArticleListing from "../models/ArticleListing";
import { GlobalSearchContext } from "../models/GlobalSearchContext";
import { GlobalSearchFilterInput } from "../models/GlobalSearchFilterInput";
import languageService from "./LanguageService";
import globalSearchApi from "../api/GlobalSearchApi";

export class GlobalSearchService {
  public context: GlobalSearchContext;
  public filters: GlobalSearchFilterInput;
  private api: IGlobalSearchApi;
  private observers: Array<() => void> = [];

  // NEW: track loading state
  public isLoading: boolean = false;

  constructor(api: IGlobalSearchApi) {
    this.api = api;
    this.context = this.defaultContext;
    this.filters = {
      term: "",
      dateFrom: null,
      dateTo: null,
      documentTypes: [],
      industries: [],
      categories: [],
      networks: [],
      ancestorId: null,
      onlyRead: null,
      orderBy: 0,
      orderByAsc: false,
      courts: [],
      editedDateFrom: null,
      editedDateTo: null,
      legislationStatus: [],
      ids: [],
    };
  }

  // NEW: hasMore helper
  public hasMore(): boolean {
    return this.context.currentPage < this.context.totalPages;
  }

  public async search(
    filters: GlobalSearchFilterInput,
    page: number | null,
    documentType: string[] | null,
    categories: string[] | null,
    language: string
  ): Promise<AxiosResponse<any, any>> {
    this.filters = filters;

    // Resolve target page (default 1)
    const targetPage = page ?? 1;

    // Indicate loading started
    this.isLoading = true;
    this.notifyObservers();

    // Perform the API call
    const response = await this.api.search(
      filters,
      targetPage,
      documentType,
      categories,
      language
    );

    // Update context (append if page > 1)
    this.updateContextWithSearchResults(response, targetPage);

    // Indicate loading finished
    this.isLoading = false;
    this.notifyObservers();

    return response;
  }

  public async refreshSearch(): Promise<AxiosResponse<any, any>> {
    // Always refresh from page 1
    return await this.search(
      this.filters,
      1,
      null,
      null,
      languageService.getCurrentInShortFormat()
    );
  }

  // NEW: loadNextPage helper for infinite scroll
  public async loadNextPage(): Promise<void> {
    if (this.isLoading || !this.hasMore()) return;
    const nextPage = (this.context.currentPage || 1) + 1;
    await this.search(
      this.filters,
      nextPage,
      null,
      null,
      languageService.getCurrentInShortFormat()
    );
  }

  // UPDATED: now accepts 'page' and appends results when page > 1
  private updateContextWithSearchResults(
    response: AxiosResponse<any, any>,
    page: number
  ): void {
    const data = response.data as Record<string, unknown>;
    this.context.filterToShow =
      (data.filterNameToShow as string[]) ??
      (data.FilterNameToShow as string[]);
    this.context.totalPages = (data.totalPages ?? data.TotalPages) as number;
    this.context.totalCount = (data.totalCount ?? data.TotalCount) as number;

    const incoming = (data.results ?? data.Results) as ArticleListing[];

    if (page > 1) {
      this.context.results = [...this.context.results, ...incoming];
    } else {
      this.context.results = incoming;
    }
    this.context.currentPage = page;

    // Update facets in the context
    Object.keys(data)
      .filter((key) => key.endsWith("Facet") && data[key])
      .forEach((key) => {
        const facetData = data[key] as Record<string, unknown>;
        const facetName = (facetData.name ?? facetData.Name) as string | undefined;
        const typePairs =
          facetData.typeCountPairs ?? facetData.TypeCountPairs;
        if (facetName) {
          this.context.facets[facetName] = (typePairs ??
            facetData) as unknown as GlobalSearchContext["facets"][string];
        }
        this.context.facets[key] =
          facetData as unknown as GlobalSearchContext["facets"][string];
      });
  }

  public setFilters(filters: GlobalSearchFilterInput): void {
    this.filters = filters;
  }

  public setSearchTerm(searchTerm: string): void {
    this.filters = { ...this.filters, term: searchTerm };
  }

  public resetContext(): void {
    this.context = this.defaultContext;
    this.notifyObservers();
  }

  public resetFilters(): void {
    this.filters = {
      ...this.filters,
      dateFrom: null,
      dateTo: null,
      industries: [],
      categories: [],
      networks: [],
      onlyRead: null,
      courts: [],
      editedDateFrom: null,
      editedDateTo: null,
      legislationStatus: [],
      ids: [],
      orderBy: 0,
      orderByAsc: true,
    };
    this.notifyObservers();
  }

  public filtersSet(): boolean {
    return (
      this.filters.term != "" ||
      (this.filters.categories != null &&
        this.filters.categories?.length > 0) ||
      (this.filters.documentTypes != null &&
        this.filters.documentTypes?.length > 0) ||
      this.filters.dateFrom != null ||
      this.filters.dateTo != null ||
      (this.filters.industries != null &&
        this.filters.industries?.length > 0) ||
      (this.filters.networks != null && this.filters.networks?.length > 0) ||
      (this.filters.courts != null && this.filters.courts?.length > 0)
    );
  }

  private notifyObservers(): void {
    this.observers.forEach((callback) => callback());
  }

  public addObserver(callback: () => void): void {
    this.observers.push(callback);
  }

  public removeObserver(callback: () => void): void {
    this.observers = this.observers.filter((obs) => obs !== callback);
  }

  private defaultContext: GlobalSearchContext = {
    filterToShow: [],
    facets: {},
    totalPages: 0,
    totalCount: 0,
    currentPage: 0,
    results: [],
  };
}

export const globalSearchService = new GlobalSearchService(globalSearchApi);
